import { HMSMessage } from '@100mslive/react-sdk';
import { getCommonAxiosCommon, UrlBuilder } from 'app/helpers/MiscUtils';
import { NgAPIResponse } from 'app/types';
import axios from 'axios';
import { config } from 'config';
import { stringify } from 'zipson';

import {
  InterviewSession,
  PaginatedRecord,
  UpdateInterviewSessionInput,
  VideoSessionStatus
} from '../API';

const EndPoints = {
  InterviewSessionByJobFairIdAndId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/:id`,
  InterviewSessionsListByJobFairIdAndCandidateId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/byCandidateId/:candidateId`,
  InterviewSessionsListByJobFairIdAndRecruiterId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/byRecruiterId/:recruiterId`,
  InterviewSessionsListByJobFairIdAndBoothId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/byBoothId/:boothId`,
  GetExcelByJobFairIdAndBoothId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/excel/byBoothId/:boothId`,
  InterviewSessionsListByJobFairIdAndQueueUserId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/byQueueUserId/:queueUserId`,
  InterviewSessionsListByJobFairId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions`,
  InterviewSessionsSignatureByJobFairId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/signature`,
  Get100msAuthTokenByJobFairId: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/InterviewSessions/auth-token`,
  BatchGetReviews: `${config.vjfpNGRestAPI}/jobfairs/:jobFairId/interviewSessions/list/reviews/batch`
};
export class InterviewSessionService {
  async getById (jobFairId: string, id: string) {
    const result = await axios.get<NgAPIResponse<InterviewSession>>(
      UrlBuilder(EndPoints.InterviewSessionByJobFairIdAndId, { jobFairId, id }),
      await getCommonAxiosCommon()
    );

    return result.data?.data;
  }

  async getActiveInterviewsByRecruiter (jobFairId: string, recruiterId: number) {
    const result = await axios.post<NgAPIResponse<InterviewSession[]>>(
      UrlBuilder(EndPoints.InterviewSessionsListByJobFairIdAndRecruiterId + '?filter=active', {
        jobFairId,
        recruiterId
      }),
      { lastEvaluatedKey: null },
      await getCommonAxiosCommon()
    );
    return result.data?.data;
  }

  async getInterviewsByRecruiter (jobFairId: string, recruiterId: number, limit: number, lastEvaluatedKey?: any) {
    const result = await axios.post<NgAPIResponse<PaginatedRecord<InterviewSession[]>>>(
      UrlBuilder(EndPoints.InterviewSessionsListByJobFairIdAndRecruiterId, {
        jobFairId,
        recruiterId
      }),
      { limit, lastEvaluatedKey },
      await getCommonAxiosCommon()
    );
    return result.data?.data;
  }

  async getExcelInterviewsByBoothId (jobFairId: string, boothId: string) {
    const result = await axios.get<NgAPIResponse<string>>(
      UrlBuilder(EndPoints.GetExcelByJobFairIdAndBoothId, {
        jobFairId,
        boothId
      }),
      await getCommonAxiosCommon()
    );
    return result.data?.data;
  }

  async getInterviewsByBoothId (jobFairId: string, boothId: string, limit: number, lastEvaluatedKey?: any) {
    const url = EndPoints.InterviewSessionsListByJobFairIdAndBoothId;

    const result = await axios.post<NgAPIResponse<PaginatedRecord<InterviewSession[]>>>(
      UrlBuilder(url, {
        jobFairId,
        boothId
      }),
      {
        limit,
        lastEvaluatedKey
      },
      await getCommonAxiosCommon()
    );

    return result.data?.data;
  }

  async getInterviewsByJobseeker (jobFairId: string, candidateId: number) {
    const result = await axios.get<NgAPIResponse<InterviewSession[]>>(
      UrlBuilder(EndPoints.InterviewSessionsListByJobFairIdAndCandidateId, {
        jobFairId,
        candidateId
      }),
      await getCommonAxiosCommon()
    );

    return result.data?.data;
  }

  async getActiveInterviewByJobseeker (jobFairId: string, candidateId: number) {
    const result = await axios.get<NgAPIResponse<InterviewSession[]>>(
      UrlBuilder(EndPoints.InterviewSessionsListByJobFairIdAndCandidateId + '?filter=active', {
        jobFairId,
        candidateId
      }),
      await getCommonAxiosCommon()
    );

    return result.data?.data;
  }

  async updateChat (
    jobFairId: string,
    id: string,
    messages: HMSMessage[],
    recruiterZoomId: number,
    jobSeekerZoomId?: number
  ) {
    return this._update(jobFairId, id, {
      id,
      recruiterZoomId,
      jobSeekerZoomId,
      chatTranscript: stringify(messages)
    });
  }

  async updateDurationAdded (jobFairId: string, id: string, durationAdded: number) {
    return this._update(jobFairId, id, {
      id,
      durationAdded
    });
  }

  async get100msAuthToken (
    jobFairId: string,
    userId: string,
    roomId: string,
    role: 'candidate' | 'interviewer'
  ) {
    const result = await axios.post<NgAPIResponse<string>>(
      UrlBuilder(EndPoints.Get100msAuthTokenByJobFairId, { jobFairId }),
      {
        roomId,
        userId,
        role
      },
      await getCommonAxiosCommon()
    );
    return result.data.data;
  }

  async updateStatus (jobFairId: string, id: string, status: VideoSessionStatus) {
    const input: UpdateInterviewSessionInput = { id, status };
    if (status === VideoSessionStatus.IN_PROGRESS) {
      input.interviewStartedAt = Math.round(new Date().getTime() / 1000);
    }
    return this._update(jobFairId, id, input);
  }

  async getReviewsBySessionIds (jobFairId: string, items: { jobFairId: string, interviewSessionId: string, recruiterId: number }[]) {
    try {
      const result = await axios.post<NgAPIResponse<any[]>>(
        UrlBuilder(EndPoints.BatchGetReviews, { jobFairId }),
        { items },
        await getCommonAxiosCommon()
      );
      return result.data?.data || [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }

  private async _update (
    jobFairId: string,
    id: string,
    updateInput: UpdateInterviewSessionInput
  ): Promise<InterviewSession | undefined> {
    try {
      const result = await axios.put<NgAPIResponse<InterviewSession>>(
        UrlBuilder(EndPoints.InterviewSessionByJobFairIdAndId, { jobFairId, id }),
        updateInput,
        await getCommonAxiosCommon()
      );

      if (!result.data?.isSuccess) {
        console.error(result.data.errors);
      }

      return result.data?.data;
    } catch (err) {
      console.log(err);
    }
  }
}
